/** @format */

import { type Aggregation, type Aggregations, type SearchParams } from "@question-bank/shared";

interface AdminQuestionFilterProps {
	aggregations?: Aggregations;
	filters: SearchParams;
	onChange: (updated: Partial<SearchParams>) => void;
}

const hasActiveFilters = (filters: SearchParams): boolean =>
	!!(filters.subject || filters.level || filters.paperCode || filters.year || filters.status);

const FIELD_CLASS =
	"w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-(--admin-color-primary-strong) focus:outline-none focus:ring-1 focus:ring-(--admin-color-primary-strong)";

interface FilterFieldProps {
	label: string;
	children: React.ReactNode;
}

const FilterField = ({ label, children }: FilterFieldProps) => (
	<div className="flex min-w-40 flex-1 flex-col gap-1">
		<span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
		{children}
	</div>
);

interface SingleSelectProps {
	items?: Aggregation[];
	value: string | undefined;
	placeholder: string;
	onChange: (value: string) => void;
}

const SingleSelect = ({ items, value, placeholder, onChange }: SingleSelectProps) => (
	<select value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={FIELD_CLASS}>
		<option value="">{placeholder}</option>
		{items?.map((item) => (
			<option key={item.key} value={item.key}>
				{item.key} ({item.count.toLocaleString()})
			</option>
		))}
	</select>
);

export const AdminQuestionFilters = ({ aggregations, filters, onChange }: AdminQuestionFilterProps) => {
	const handleClearAll = () => {
		onChange({
			subject: undefined,
			level: undefined,
			paperCode: undefined,
			year: undefined,
			status: undefined,
		});
	};

	return (
		<div className="rounded-lg border border-slate-200 bg-white p-4 shadow-(--admin-shadow-raised)">
			<div className="mb-4 flex items-center justify-between">
				<h2 className="text-sm font-semibold text-slate-800">Filters</h2>
				{hasActiveFilters(filters) && (
					<button
						type="button"
						onClick={handleClearAll}
						className="text-xs font-medium text-(--admin-color-primary-strong) hover:underline"
					>
						Clear all
					</button>
				)}
			</div>

			<div className="flex flex-wrap items-end gap-4">
				<FilterField label="Subject">
					<SingleSelect
						items={aggregations?.subject}
						value={filters.subject}
						placeholder="All subjects"
						onChange={(value) => onChange({ subject: value || undefined })}
					/>
				</FilterField>

				<FilterField label="Level">
					<SingleSelect
						items={aggregations?.level}
						value={filters.level}
						placeholder="All levels"
						onChange={(value) => onChange({ level: value || undefined })}
					/>
				</FilterField>

				<FilterField label="Year">
					<select
						value={filters.year ? String(filters.year) : ""}
						onChange={(e) => onChange({ year: e.target.value ? Number(e.target.value) : undefined })}
						className={FIELD_CLASS}
					>
						<option value="">All years</option>
						{aggregations?.year?.map((item) => (
							<option key={item.key} value={item.key}>
								{item.key} ({item.count.toLocaleString()})
							</option>
						))}
					</select>
				</FilterField>

				<FilterField label="Paper Code">
					<SingleSelect
						items={aggregations?.paperCode}
						value={filters.paperCode}
						placeholder="All paper codes"
						onChange={(value) => onChange({ paperCode: value || undefined })}
					/>
				</FilterField>

				<FilterField label="Status">
					<select
						value={filters.status ?? ""}
						onChange={(e) => onChange({ status: (e.target.value || undefined) as SearchParams["status"] })}
						className={FIELD_CLASS}
					>
						<option value="">All statuses</option>
						<option value="draft">Draft</option>
						<option value="staged">Staged</option>
						<option value="published">Published</option>
					</select>
				</FilterField>
			</div>
		</div>
	);
};
